"use client"

import { useEffect, useState } from "react"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { useCookieConsent } from "./consent-context"
import { isHeatmapPreview } from "@modules/page-analytics/preview"

export default function CookieConsentBanner() {
  const {
    hasDecided,
    acceptAll,
    rejectAll,
    openPreferences,
  } = useCookieConsent()

  // Hide the banner when the page is rendered inside the admin heatmap viewer
  // so it doesn't float over the page being analysed.
  const [preview, setPreview] = useState(false)
  useEffect(() => {
    setPreview(isHeatmapPreview())
  }, [])
  if (preview) return null

  // Don't render anything until we've read the cookie (hasDecided reflects
  // either "we have a saved choice" or "we know there isn't one yet").
  // While `consent === null` and `hasDecided === false` the banner is hidden;
  // a useEffect inside the provider flips it on first paint.
  if (hasDecided) return null

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[80] border-t border-aov-border bg-white shadow-[0_-8px_32px_rgba(27,42,74,0.10)]"
    >
      <div className="content-container py-5">
        <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between md:gap-8">
          <div className="max-w-2xl">
            <h2 className="font-display text-lg font-bold uppercase tracking-wide text-aov-navy mb-2">
              We use cookies
            </h2>
            <p className="text-sm text-aov-text-muted leading-relaxed">
              We use essential cookies to make the site work, and (with your
              permission) analytics and marketing cookies so we can understand
              how visitors use the site and measure the performance of our
              campaigns. See our{" "}
              <LocalizedClientLink
                href="/cookies"
                className="font-semibold text-aov-orange hover:underline"
              >
                Cookie Policy
              </LocalizedClientLink>{" "}
              for details.
            </p>
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end md:flex-shrink-0">
            <button
              type="button"
              onClick={openPreferences}
              className="inline-flex items-center justify-center rounded-[10px] border border-aov-navy/20 px-5 py-2.5 text-sm font-bold text-aov-navy transition-colors hover:bg-aov-navy/5"
            >
              Customise
            </button>
            <button
              type="button"
              onClick={rejectAll}
              className="inline-flex items-center justify-center rounded-[10px] border border-aov-navy/20 px-5 py-2.5 text-sm font-bold text-aov-navy transition-colors hover:bg-aov-navy/5"
            >
              Reject non-essential
            </button>
            <button
              type="button"
              onClick={acceptAll}
              className="inline-flex items-center justify-center rounded-[10px] bg-aov-orange px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-aov-orange/90"
            >
              Accept all
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
